import type { NodeMetrics, EdgeMetrics } from '@/domain/metrics/contracts';
import type { PropagationEdgeInput } from '@/domain/metrics/propagation/propagate';
import type { Scenario, SimulationGraphInput, SimulationNodeInput } from '../contracts';

/**
 * SPEC-009 §57/§58/plan §9/SIM-PRINCIPLE-003/METRIC-REQ-012-style purity —
 * overlays a scenario's overrides onto a plain graph input WITHOUT
 * mutating it. The base graph object is never touched: every node/edge
 * that has an override gets a brand-new `metrics` object; everything else
 * is passed through by reference (cheap, and still safe since nothing
 * downstream mutates it either).
 */
export function applyScenario(graph: SimulationGraphInput, scenario: Scenario | null): SimulationGraphInput {
  if (!scenario) return graph;

  const nodeOverrides = scenario.overrides.nodeOverrides;
  const edgeOverrides = scenario.overrides.edgeOverrides;

  const nodes: SimulationNodeInput[] = graph.nodes.map((node) => {
    const override = nodeOverrides[node.id];
    if (!override) return node;
    const metrics: NodeMetrics = { ...node.metrics, ...override };
    return { ...node, metrics };
  });

  const edges: PropagationEdgeInput[] = graph.edges.map((edge) => {
    const override = edgeOverrides[edge.id];
    if (!override?.conversion) return edge;
    const metrics: EdgeMetrics = { ...edge.metrics, conversion: override.conversion };
    return { ...edge, metrics };
  });

  return { nodes, edges };
}
